import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  getMoleculeComponents,
  type MoleculeComponentEntry,
  type MoleculeComponents
} from '@peruselab/api-client/molecules';
import { moleculePagePath, usePublicAccess } from '../../public/publicAccess';
import { FormulaText, withCharge } from '../../../components/FormulaText';

/**
 * #1086 (owner): a salt's page lists what it's made of (CH₃CH₂O⁻ and Na⁺, with counts), and an
 * ion's page the salts it's part of -- each a link to that molecule's page. Nothing when neither.
 * #1115: a salt lists all its ions; one the reader may not open is shown, not linked.
 */
export function SaltLinks({ moleculeId }: { readonly moleculeId: string }) {
  const { t } = useTranslation();
  const { signedIn } = usePublicAccess();
  const [links, setLinks] = useState<MoleculeComponents | null>(null);

  useEffect(() => {
    let current = true;
    setLinks(null);
    getMoleculeComponents(moleculeId)
      .then((answer) => current && setLinks(answer))
      .catch(() => current && setLinks(null));
    return () => {
      current = false;
    };
  }, [moleculeId]);

  if (!links || (links.components.length === 0 && links.salts.length === 0)) return null;
  const item = (entry: MoleculeComponentEntry, index: number, counted: boolean) => {
    const formula = entry.molecularFormula ? (
      <FormulaText formula={withCharge(entry.molecularFormula, entry.formalCharge)} />
    ) : null;
    const label = entry.name ?? formula ?? entry.id;
    return (
      <li key={entry.id ?? `ion-${index}`}>
        {/* #1115: an ion the reader may not open is named by its formula, without a link. */}
        {entry.id ? <Link to={moleculePagePath(entry.id, signedIn)}>{label}</Link> : <span>{label}</span>}
        {counted && entry.count > 1 ? <span className="salt-links-count"> × {entry.count}</span> : null}
      </li>
    );
  };
  return (
    <div className="salt-links" data-testid="salt-links">
      {links.components.length > 0 ? (
        <section aria-label={t('moleculePage.salt.madeOf')}>
          <h3>{t('moleculePage.salt.madeOf')}</h3>
          <ul>{links.components.map((entry, index) => item(entry, index, true))}</ul>
        </section>
      ) : null}
      {links.salts.length > 0 ? (
        <section aria-label={t('moleculePage.salt.partOf')}>
          <h3>{t('moleculePage.salt.partOf')}</h3>
          <ul>{links.salts.map((entry, index) => item(entry, index, false))}</ul>
        </section>
      ) : null}
    </div>
  );
}
